/**
 * Armadillo Security Middleware
 * Phoenix Architecture: Wraps all API calls with security protection
 * 
 * This is the CENTRAL security layer that intercepts all user input
 * before it reaches the database.
 */

import { armadilloAPI } from './armadillo';
import { toast } from 'sonner';
import { normalizeProse, validateProse } from '@/lib/prose';
export { validateProse } from '@/lib/prose';

// ============================================
// SECURITY CONTEXT
// ============================================

export interface SecurityContext {
  userId: string | null;
  action: string;
  input: string | object;
  timestamp: string;
}

export interface SecurityResult {
  allowed: boolean;
  reason?: string;
  sanitizedInput?: any;
  threatDetected?: boolean;
}

// ============================================
// INPUT SANITIZATION
// ============================================

/**
 * Sanitize user input - removes dangerous patterns
 */
export const sanitizeInput = (input: string): string => {
  if (!input) return '';
  
  return normalizeProse(input.replace(/\0/g, '')).trim();
};

/**
 * Sanitize object inputs recursively
 */
export const sanitizeObject = (obj: Record<string, any>): Record<string, any> => {
  const sanitized: Record<string, any> = {};
  
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string') {
      sanitized[key] = sanitizeInput(value);
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeObject(value);
    } else {
      sanitized[key] = value;
    }
  }
  
  return sanitized;
};

// ============================================
// SECURITY VALIDATION
// ============================================

/**
 * Validate and protect any user input
 * Returns sanitized input AND logs threats
 */
export const validateInput = async (
  input: string,
  userId: string | null,
  context: string
): Promise<SecurityResult> => {
  const sanitized = sanitizeInput(input);
  
  // Check for SQL injection
  const hasSQLInjection = await armadilloAPI.checkSQLInjection(sanitized, userId);
  if (hasSQLInjection) {
    await armadilloAPI.logSecurityEvent(
      userId,
      'sql_injection_blocked',
      'critical',
      `SQL injection attempt blocked in ${context}`,
      { context, inputLength: sanitized.length }
    );
    
    // Quarantine in threat capsule
    await armadilloAPI.createThreatCapsule(userId, {
      type: 'sql_injection',
      context,
      inputLength: sanitized.length,
      timestamp: new Date().toISOString()
    });
    
    return {
      allowed: false,
      reason: 'Invalid input detected',
      threatDetected: true
    };
  }
  
  // Check for XSS
  const hasXSS = await armadilloAPI.checkXSS(sanitized, userId);
  if (hasXSS) {
    await armadilloAPI.logSecurityEvent(
      userId,
      'xss_attack_blocked',
      'critical',
      `XSS attack attempt blocked in ${context}`,
      { context, inputLength: sanitized.length }
    );
    
    // Quarantine in threat capsule
    await armadilloAPI.createThreatCapsule(userId, {
      type: 'xss_attack',
      context,
      inputLength: sanitized.length,
      timestamp: new Date().toISOString()
    });
    
    return {
      allowed: false,
      reason: 'Invalid input detected',
      threatDetected: true
    };
  }
  
  // Check for Path Traversal
  const hasPathTraversal = await armadilloAPI.checkPathTraversal(sanitized, userId);
  if (hasPathTraversal) {
    await armadilloAPI.logSecurityEvent(
      userId,
      'path_traversal_blocked',
      'high',
      `Path traversal attempt blocked in ${context}`,
      { context, inputLength: sanitized.length }
    );
    
    await armadilloAPI.createThreatCapsule(userId, {
      type: 'path_traversal',
      context,
      inputLength: sanitized.length,
      timestamp: new Date().toISOString()
    });
    
    return {
      allowed: false,
      reason: 'Invalid input detected',
      threatDetected: true
    };
  }
  
  // Check for Command Injection
  const hasCmdInjection = await armadilloAPI.checkCommandInjection(sanitized, userId);
  if (hasCmdInjection) {
    await armadilloAPI.logSecurityEvent(
      userId,
      'command_injection_blocked',
      'critical',
      `Command injection attempt blocked in ${context}`,
      { context, inputLength: sanitized.length }
    );
    
    await armadilloAPI.createThreatCapsule(userId, {
      type: 'command_injection',
      context,
      inputLength: sanitized.length,
      timestamp: new Date().toISOString()
    });
    
    return {
      allowed: false,
      reason: 'Invalid input detected',
      threatDetected: true
    };
  }
  
  return {
    allowed: true,
    sanitizedInput: sanitized
  };
};

/**
 * Validate object inputs
 */
export const validateObject = async (
  obj: Record<string, any>,
  userId: string | null,
  context: string
): Promise<SecurityResult> => {
  const sanitized = sanitizeObject(obj);
  
  // Check each string field
  for (const [key, value] of Object.entries(obj)) {
    if (typeof value === 'string' && value.length > 0) {
      const result = await validateInput(value, userId, `${context}.${key}`);
      if (!result.allowed) {
        return result;
      }
    }
  }
  
  return {
    allowed: true,
    sanitizedInput: sanitized
  };
};

// ============================================
// RATE LIMITING WRAPPER
// ============================================

/**
 * Check rate limit before critical actions
 */
export const checkRateLimit = async (
  userId: string,
  action: string,
  maxRequests: number = 30
): Promise<boolean> => {
  const identifier = userId || action;
  const result = await armadilloAPI.checkRateLimit(identifier, maxRequests, 60);
  
  if (!result.allowed) {
    await armadilloAPI.logSecurityEvent(
      userId,
      'rate_limit_exceeded',
      'medium',
      `Rate limit exceeded for action: ${action}`,
      { action, remaining: result.remaining }
    );
    
    toast.error('Too many requests. Please slow down.');
    return false;
  }
  
  return true;
};

// ============================================
// TRUST SCORE INTEGRATION
// ============================================

/**
 * Update trust score based on user actions
 */
export const updateTrustForAction = async (
  userId: string,
  action: string,
  success: boolean
) => {
  let scoreChange = 0;
  let reason = '';
  
  if (success) {
    switch (action) {
      case 'bounty_posted':
        scoreChange = 1;
        reason = 'Bounty posted successfully';
        break;
      case 'claim_approved':
        scoreChange = 2;
        reason = 'Claim approved';
        break;
      case 'proof_submitted':
        scoreChange = 2;
        reason = 'Proof submitted';
        break;
      case 'profile_updated':
        scoreChange = 1;
        reason = 'Profile updated';
        break;
      case 'chat_message':
        scoreChange = 0.5;
        reason = 'Chat message sent';
        break;
      default:
        scoreChange = 0.5;
        reason = `Action completed: ${action}`;
    }
  } else {
    scoreChange = -2;
    reason = `Failed action: ${action}`;
  }
  
  if (scoreChange !== 0) {
    const current = await armadilloAPI.getTrustScore(userId);
    const newScore = Math.max(0, Math.min(100, (current?.score || 25) + scoreChange));
    await armadilloAPI.updateTrustScore(userId, newScore, reason);
  }
};

// ============================================
// SECURITY AUDIT WRAPPER
// ============================================

/**
 * Wrap any async action with full security audit
 */
export const withSecurityAudit = async <T>(
  userId: string | null,
  action: string,
  context: string,
  fn: () => Promise<T>,
  options: {
    checkRateLimit?: boolean;
    maxRequests?: number;
    trackTrust?: boolean;
    successAction?: string;
    failureAction?: string;
  } = {}
): Promise<T> => {
  const {
    checkRateLimit: shouldCheckRateLimit = false,
    maxRequests = 30,
    trackTrust = false,
    successAction,
    failureAction
  } = options;
  
  // 1. Check rate limit if required
  if (shouldCheckRateLimit && userId) {
    const allowed = await checkRateLimit(userId, action, maxRequests);
    if (!allowed) {
      throw new Error('Rate limit exceeded');
    }
  }
  
  // 2. Log action start
  await armadilloAPI.logSecurityEvent(
    userId,
    `${action}_started`,
    'low',
    `Action initiated: ${action}`,
    { context }
  );
  
  try {
    // 3. Execute the action
    const result = await fn();
    
    // 4. Track trust if enabled
    if (trackTrust && userId && successAction) {
      await updateTrustForAction(userId, successAction, true);
    }
    
    // 5. Log success
    await armadilloAPI.logSecurityEvent(
      userId,
      `${action}_completed`,
      'low',
      `Action completed: ${action}`,
      { context, success: true }
    );
    
    return result;
    
  } catch (error: any) {
    // 6. Track failure
    if (trackTrust && userId && failureAction) {
      await updateTrustForAction(userId, failureAction, false);
    }
    
    // 7. Log failure
    await armadilloAPI.logSecurityEvent(
      userId,
      `${action}_failed`,
      'medium',
      `Action failed: ${action}`,
      { context, error: error.message }
    );
    
    throw error;
  }
};

// ============================================
// PROTECTED FORM HANDLER
// ============================================

export interface FormSecurityResult {
  safe: boolean;
  data?: Record<string, any>;
  errors?: string[];
}

/**
 * Process form data with full security validation
 */
export const secureFormData = async (
  data: Record<string, any>,
  userId: string | null,
  context: string
): Promise<FormSecurityResult> => {
  const errors: string[] = [];
  
  // Validate all string fields
  for (const [key, value] of Object.entries(data)) {
    if (typeof value === 'string' && value.trim().length > 0) {
      const result = await validateInput(value, userId, `${context}.${key}`);
      if (!result.allowed) {
        errors.push(`Invalid input in field: ${key}`);
      }
    }
  }
  
  if (errors.length > 0) {
    return { safe: false, errors };
  }
  
  return {
    safe: true,
    data: sanitizeObject(data)
  };
};

// ============================================
// SESSION SECURITY
// ============================================

/**
 * Generate device fingerprint
 */
export const getDeviceFingerprint = (): string => {
  if (typeof window === 'undefined') return '';
  
  const components = [
    navigator.userAgent,
    navigator.language,
    screen.width + 'x' + screen.height,
    new Date().getTimezoneOffset(),
    navigator.hardwareConcurrency || 1,
    (navigator as any).deviceMemory || 4
  ];
  
  // Simple hash
  let hash = 0;
  const str = components.join('|');
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  
  return `dev_${Math.abs(hash).toString(36)}`;
};

/**
 * Check if user session is secure with enhanced validation
 */
export const validateSession = async (userId: string): Promise<{
  secure: boolean;
  trustScore: number;
  tier: string;
  deviceFingerprint: string;
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
}> => {
  const trustData = await armadilloAPI.getTrustScore(userId);
  const deviceFingerprint = getDeviceFingerprint();
  
  // Calculate risk level
  const score = trustData?.score || 0;
  let riskLevel: 'low' | 'medium' | 'high' | 'critical' = 'low';
  
  if (score < 10) riskLevel = 'critical';
  else if (score < 30) riskLevel = 'high';
  else if (score < 50) riskLevel = 'medium';
  
  return {
    secure: score > 10,
    trustScore: score,
    tier: trustData?.tier_name || 'Untrusted',
    deviceFingerprint,
    riskLevel
  };
};

// ============================================
// BEHAVIORAL ANALYTICS
// ============================================

/**
 * Track user behavior for anomaly detection
 */
export const trackBehavior = async (
  userId: string,
  action: string,
  metadata?: Record<string, any>
): Promise<void> => {
  try {
    // Log to security events for now (can be expanded to dedicated table)
    await armadilloAPI.logSecurityEvent(
      userId,
      `behavior_${action}`,
      'low',
      `User behavior tracked: ${action}`,
      {
        ...metadata,
        deviceFingerprint: getDeviceFingerprint(),
        timestamp: new Date().toISOString()
      }
    );
  } catch (error) {
    console.error('Error tracking behavior:', error);
  }
};

/**
 * Detect anomalous behavior patterns
 */
export const detectAnomalies = async (
  userId: string,
  recentActions: Array<{ action: string; timestamp: string }>
): Promise<{ anomalous: boolean; reasons: string[] }> => {
  const reasons: string[] = [];
  
  // Check for rapid-fire actions (>10 actions in 60 seconds)
  const now = Date.now();
  const recentActionsInWindow = recentActions.filter(a => 
    now - new Date(a.timestamp).getTime() < 60000
  );
  
  if (recentActionsInWindow.length > 10) {
    reasons.push(`Rapid action detection: ${recentActionsInWindow.length} actions in 60s`);
  }
  
  // Check for repetitive same action (>5 times in 30 seconds)
  const actionCounts: Record<string, number> = {};
  recentActions.forEach(a => {
    if (now - new Date(a.timestamp).getTime() < 30000) {
      actionCounts[a.action] = (actionCounts[a.action] || 0) + 1;
    }
  });
  
  for (const [action, count] of Object.entries(actionCounts)) {
    if (count > 5) {
      reasons.push(`Repetitive action: ${action} executed ${count} times in 30s`);
    }
  }
  
  return {
    anomalous: reasons.length > 0,
    reasons
  };
};

/**
 * Calculate behavioral risk score
 */
export const calculateBehavioralRisk = async (
  userId: string,
  actions: Array<{ action: string; timestamp: string }>
): Promise<number> => {
  const { anomalous, reasons } = await detectAnomalies(userId, actions);
  
  if (!anomalous) return 0;
  
  // Base risk from anomaly count
  let risk = reasons.length * 5;
  
  // Check trust score
  const trustData = await armadilloAPI.getTrustScore(userId);
  if ((trustData?.score || 25) < 30) {
    risk += 10; // Low trust users get higher risk
  }
  
  return Math.min(100, risk);
};

// ============================================
// EXPORT ALL SECURITY UTILITIES
// ============================================

export const armadillo = {
  // Core Validation (5)
  sanitizeInput,
  sanitizeObject,
  validateInput,
  validateProse,
  validateObject,
  secureFormData,
  
  // Rate Limiting (2)
  checkRateLimit,
  resetRateLimit: armadilloAPI.resetRateLimit,
  
  // Trust Management (1)
  updateTrustForAction,
  
  // Session Security (2)
  validateSession,
  getDeviceFingerprint,
  
  // Behavioral Analytics (3)
  trackBehavior,
  detectAnomalies,
  calculateBehavioralRisk,
  
  // Threat Patterns (2)
  getThreatPatterns: armadilloAPI.getThreatPatterns,
  updateThreatPattern: armadilloAPI.updateThreatPattern,
  
  // Advanced Stats (2)
  getUserThreatStats: armadilloAPI.getUserThreatStats,
  getRecentThreats: armadilloAPI.getRecentThreats,
  
  // Audit Wrapper (1)
  withSecurityAudit,
  
  // Threat Capsules (4) - Direct access
  createThreatCapsule: armadilloAPI.createThreatCapsule,
  getThreatCapsules: armadilloAPI.getThreatCapsules,
  releaseThreatCapsule: armadilloAPI.releaseThreatCapsule,
  cleanupExpiredCapsules: armadilloAPI.cleanupExpiredCapsules,
  
  // Direct API Access (all 23)
  api: armadilloAPI
};